import Grid from "@mui/material/Grid/Grid";
import {AgGridReact} from "ag-grid-react/lib/agGridReact";
import {RefObject, useCallback, useEffect, useRef, useState} from "react";
import {useRecoilState} from "recoil";
import {ColDef, ProcessCellForExportParams} from "ag-grid-community";
import {GridApi} from "ag-grid-enterprise";
import {useAgGridLocale} from "../../../../../config/globalConfig";
import {BASE_DEFINITION_FORM_STEPS, DEFAULT_MATRIX_COL_DEF,} from "../../../constants";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import PercentageCellEditor from "../../../common/components/PercentageCellEditor";
import {
    GreenDiscountDetails,
    GreenDiscountMatrixRecord,
    GreenDiscountUpdateDetails,
} from "../../../api/types/greenDiscount";
import {useGreenDiscountDetailRepository} from "../../../api/repositories/GreenDiscountDetailRepository";
import {acrissListAtom, greenDiscountCreationAtom,} from "../../../state/greenDiscount";
import {useMatrixColDefsGreen} from "./useMatrixColDefsGreen";

const GreenDiscountMatrix = ({
                                 submitRef,
                             }: {
    submitRef: RefObject<HTMLButtonElement>;
}) => {
    // Use State
    const [rowData, setRowData] = useState<
        { [key: string]: number | string | null | GreenDiscountDetails }[]
    >([]);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);

    const onGridReady = (params: any) => {
        setGridApi(params.api);
    };

    // Use Repository Hooks
    const {updateGreenDiscountMatrix} = useGreenDiscountDetailRepository();

    // use Recoil
    const [greenDiscountCreation, setGreenDiscountCreation] = useRecoilState(greenDiscountCreationAtom);
    const [acrissList] = useRecoilState(acrissListAtom)
    const {readOnly, matrixData, definitionData} = greenDiscountCreation;

    // Use Ref
    const gridRef = useRef<AgGridReact<GreenDiscountMatrixRecord>>(null);

    // agGrid
    const columnDefs: ColDef[] = useMatrixColDefsGreen({
        matrixData: matrixData as GreenDiscountDetails[],
        editable: !readOnly,
    });
    const agGridLocale = useAgGridLocale();

    const matrixInit = useCallback(() => {
        if (matrixData?.length === 0 || matrixData === undefined) {
            const rowData: {
                [key: string]: number | string | null | GreenDiscountDetails;
            }[] =
                acrissList?.map((el) => {
                    const row: {
                        [key: string]: number | string | null | GreenDiscountDetails;
                    } = {
                        acrissId: el.id as string,
                        acrissFuelType: (el.id + " - " + el.name) as string,
                        discount: 0 as number,
                    };
                    return row;
                }) || [];
            setRowData(JSON.parse(JSON.stringify(rowData)));
        } else {
            const rowData: {
                [key: string]: number | string | null | GreenDiscountDetails;
            }[] =
                matrixData?.map((el) => {
                    const row: {
                        [key: string]: number | string | null | GreenDiscountDetails;
                    } = {
                        id: el.id as number,
                        acrissId: el.acrissFuelType as string,
                        acrissFuelType: (el.acrissFuelType +
                            " - " +
                            acrissList.find((a) => a.id === el.acrissFuelType)
                                ?.name) as string,
                        discount: el.discount as number,
                    };
                    return row;
                }) || [];
            setRowData(JSON.parse(JSON.stringify(rowData)));
        }
    }, [matrixData]);

    useEffect(() => {
        matrixInit();
    }, [matrixInit]);

    const matrixConfirm = async () => {
        const request: GreenDiscountUpdateDetails = {
            prcGreenDiscountRate: {
                id: definitionData?.id as number,
                name: definitionData?.name as string,
                description: definitionData?.description as string,
                pickupFrom: definitionData?.pickupFrom as string,
                pickupTo: definitionData?.pickupTo as string,
                reservationFrom: definitionData?.reservationFrom as string,
                reservationTo: definitionData?.reservationTo as string,
                dealer: definitionData?.dealer?.label as string,
                legalEntity: definitionData?.legalEntity?.label as string,
                commercialCategory: {
                    id: definitionData?.commercialCategory?.code as string,
                    name: definitionData?.commercialCategory?.label as string,
                    active: definitionData?.commercialCategory?.active as boolean,
                    commercialSegment: {
                        id: definitionData?.commercialCategory?.commercialSegment
                            .id as string,
                        name: definitionData?.commercialCategory?.commercialSegment
                            .name as string,
                        active: definitionData?.commercialCategory?.commercialSegment
                            .active as boolean,
                    },
                },
                dealerId: 1,
                used: definitionData?.used ? definitionData?.used : false,
            },
            prcGreenDiscountRateDetails: [],
        };

        if (gridApi)
            gridApi.forEachNodeAfterFilterAndSort((node) => {
                if (node.data) {
                    const row = node.data;
                    Object.values({row}).forEach((value) => {
                        typeof value === "object" &&
                        value &&
                        request.prcGreenDiscountRateDetails.push({
                            id: (value as GreenDiscountDetails)?.id
                                ? ((value as GreenDiscountDetails)?.id as number)
                                : null,
                            discount: (value as GreenDiscountDetails)?.discount,
                            acrissFuelType: (value as GreenDiscountDetails).acrissId,
                            prcGreenDiscountRate: {
                                id: definitionData?.id as number,
                                name: definitionData?.name as string,
                                description: definitionData?.description as string,
                                pickupFrom: definitionData?.pickupFrom as string,
                                pickupTo: definitionData?.pickupTo as string,
                                reservationFrom: definitionData?.reservationFrom as string,
                                reservationTo: definitionData?.reservationTo as string,
                                dealer: definitionData?.dealer?.label as string,
                                legalEntity: definitionData?.legalEntity?.label as string,
                                commercialCategory: {
                                    id: definitionData?.commercialCategory?.code as string,
                                    name: definitionData?.commercialCategory?.label as string,
                                    active: definitionData?.commercialCategory
                                        ?.active as boolean,
                                    commercialSegment: {
                                        id: definitionData?.commercialCategory?.commercialSegment
                                            .id as string,
                                        name: definitionData?.commercialCategory
                                            ?.commercialSegment.name as string,
                                        active: definitionData?.commercialCategory
                                            ?.commercialSegment.active as boolean,
                                    },
                                },
                                dealerId: 1,
                                used: definitionData?.used ? definitionData?.used : false,
                            },
                        });
                    });
                }
            });

        try {
            await updateGreenDiscountMatrix(request);
            setGreenDiscountCreation({
                ...greenDiscountCreation,
                step: BASE_DEFINITION_FORM_STEPS.DONE,
            });
        } catch (e) {
        }
    };

    const defaultColDef = {...DEFAULT_MATRIX_COL_DEF};

    function processCellFromClipboard(params: ProcessCellForExportParams) {
        // @ts-ignore
        if (!isNaN(params.value)) {
            return params.value;
        } else {
            return "";
        }
    }

    return (
        <Grid container spacing={2} p={2}>
            <Grid item xs={12} sm={12}>
                <AgGridCustomTable
                    rowData={rowData}
                    defaultColDef={defaultColDef}
                    domLayout={"autoHeight"}
                    tooltipShowDelay={0}
                    frameworkComponents={{
                        customEditor: PercentageCellEditor,
                    }}
                    columnDefs={columnDefs}
                    gridRef={gridRef}
                    localeText={agGridLocale}
                    pagination={false}
                    stopEditingWhenCellsLoseFocus
                    onGridReady={onGridReady}
                    rowSelection={"multiple"}
                    suppressRowClickSelection={true}
                    enableRangeSelection={true}
                    enableCellTextSelection={false}
                    processCellFromClipboard={processCellFromClipboard}
                />
            </Grid>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    matrixConfirm();
                }}
            >
                <button ref={submitRef} type="submit" style={{display: "none"}}/>
            </form>
        </Grid>
    );
};

export default GreenDiscountMatrix;
